// src/components/dashboard/OrgCameraView.jsx
import { useState, useEffect } from "react";
import { FiCamera, FiVideo } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";

// ✅ Import stores
import { useAuthStore } from "../../stores/authStore";
import { useOrganizationStore } from "../../stores/organizationStore";
import { useCameraStore } from "../../stores/cameraStore";
import { useUIStore } from "../../stores/uiStore";

import Sidebar from "./Sidebar";

//import CameraWorkspace from "../../pages/Cameras/CameraWorkspace";
import CameraWorkspace from "../../pages/Cameras/EnhancedCameraWorkspace";

//import EventsSection from "./EventsSection";
import EventsSection from "../../pages/Events/EnterpriseEventsWorkspace";

import CameraModal from "./CameraModal";

import UserManager from "./OrgPlatformView/UserManager";
import OrganizationGrid from "./OrgPlatformView/OrganizationGrid";
import OrganizationDetail from "./OrgPlatformView/OrganizationDetail";
import PlatformSettings from "./OrgPlatformView/PlatformSettings";
import SystemHealth from "./OrgPlatformView/SystemHealth";

import SitesTab from "./OrgAdminView/SitesTab";
import UsersTab from "./OrgAdminView/UsersTab"; 

import useCameraManager from "./logic/useCameraManager";
import useUserManager from "./logic/useUserManager";
import useSiteManager from "./logic/useSiteManager";

import authAxios from "../../api/authAxios";

export default function OrgCameraView({
  // ✅ REMOVED: Data props (now from stores)
  // organizations, activeOrgId, setActiveOrgId, activeSiteId, setActiveSiteId, global_role, currentUser
  
  // ✅ KEPT: Callback props (they're fine)
  onAddUser,
  onEditUser,
  onDeleteUser,
  onAddSite,
  onEditSite,
  onDeleteSite,
  onAddOrg,
  onEditOrg,
  onDeleteOrg,
}) {
  const navigate = useNavigate();
  const { theme } = useTheme();

  // ==========================================
  // GET DATA FROM STORES (instead of props)
  // ==========================================
  
  // Auth store
  const currentUser = useAuthStore(s => s.currentUser);
  const activeOrgId = useAuthStore(s => s.activeOrgId);
  const activeSiteId = useAuthStore(s => s.activeSiteId);
  const setActiveOrg = useAuthStore(s => s.setActiveOrg);
  const setActiveSite = useAuthStore(s => s.setActiveSite);
  
  // Organization store
  const organizations = useOrganizationStore(s => s.organizations);
  
  // Camera store
  const streamStatus = useCameraStore(s => s.streamStatus);
  const bulkUpdateStreamStatus = useCameraStore(s => s.bulkUpdateStreamStatus);
  
  // UI store
  const activeSection = useUIStore(s => s.activeSection);
  const setActiveSection = useUIStore(s => s.setActiveSection);
  
  // Computed values
  const global_role = currentUser?.global_role;

  // ==========================================
  // LOCAL UI STATE (keep this - UI only)
  // ==========================================
  
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);

  // ==========================================
  // INITIALIZE activeSection
  // ==========================================
  
  useEffect(() => {
    if (global_role === "platform_admin" && activeSiteId && !activeSection) {
      setActiveSection("cameras");
    }
  }, [global_role, activeSiteId, activeSection, setActiveSection]);

  // ==========================================
  // DATA HOOKS (keep these - they're good!)
  // ==========================================
  
  const {
    cameras,
    form,
    setForm,
    editMode,
    setEditMode,
    editingCameraId,
    setEditingCameraId,
    loading,
    fetchCameras,
    handleAddOrEditCamera,
    startEditing,  
    startAdding, 
	  
  } = useCameraManager(activeOrgId, activeSiteId, organizations); 

  const { users, fetchUsers } = useUserManager(
    {},
    organizations,
    activeOrgId,
    global_role
  );

  const { sites, fetchSites } = useSiteManager({}, activeOrgId);

//  New (fixed) way:
const handleEditClick = (camera) => {
  startEditing(camera);  // ✅ Uses the helper that sets site!
};

//  Also fix add button:
const handleAddClick = () => {
  startAdding();  // ✅ Pre-fills site!
};


  // ==========================================
  // LOAD USERS + SITES (ORG SCOPE)
  // ==========================================
  
  useEffect(() => {
    if (!activeOrgId) return;
    fetchUsers();
    fetchSites();
  }, [activeOrgId]);

  // ==========================================
  // LOAD CAMERAS (ONLY WHEN NEEDED)
  // ==========================================
  
  useEffect(() => {
    if (!activeSiteId) return;
    fetchCameras();
  }, [activeSiteId]);

  // ==========================================
  // STREAM STATUS POLLING
  // ==========================================
  
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const res = await authAxios.get("/cameras/status/bulk");
        const map = {};
        res.data.forEach((c) => {
          map[c.id] = c.connection_status || "offline";
        });
        // ✅ Update store instead of local state
        bulkUpdateStreamStatus(map);
      } catch (err) {
        console.error("Failed to load camera statuses:", err);
      }
    };

    loadStatuses();
    const t = setInterval(loadStatuses, 10000);
    return () => clearInterval(t);
  }, [bulkUpdateStreamStatus]);

  // ==========================================
  // PERMISSIONS
  // ==========================================

  const canManageCameras =
    global_role === "platform_admin" ||
    global_role === "org_admin" ||  // ✅ ADD THIS LINE
    (organizations.find((o) => o.id === activeOrgId)?.members || []).some(
      (m) =>
        m.user.id === Number(localStorage.getItem("userId")) &&
        ["admin", "org_admin"].includes(m.role)
    );


  // ==========================================
  // LOGOUT
  // ==========================================
  
  const logout = () => {
    localStorage.clear();
    navigate("/");
  };

  // ==========================================
  // RENDER
  // ==========================================
  
  return (
    <div
      className={`min-h-screen flex ${
        theme === "dark"
          ? "bg-darkBg text-gray-100"
          : "bg-gray-50 text-gray-900"
      }`}
    >
      {/* ===== SIDEBAR ===== */}
      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        onSelectOrg={(orgId) => {
          setActiveOrg(orgId);
          localStorage.setItem("activeOrgId", String(orgId));
        }}
        onSelectSite={(orgId, siteId) => {
          setActiveOrg(orgId);
          setActiveSite(siteId);
          localStorage.setItem("activeOrgId", String(orgId));
          localStorage.setItem("activeSiteId", String(siteId));
        }}
        onLogout={logout}
      />

      {/* ===== MAIN CONTENT ===== */}
      <main className="flex-1 md:ml-64 p-6">
        {/* ===== PLATFORM ADMIN SECTIONS ===== */}

        {global_role === "platform_admin" && activeSection === "organizations" && !selectedOrg && (
          <div className="text-gray-400">
            Select an organization from the sidebar
          </div>
        )}

        {/* ===== PLATFORM ADMIN : ORGANIZATIONS ===== */}
        {global_role === "platform_admin" &&
          activeSection === "organizations" &&
          !selectedOrg && (
            <OrganizationGrid
              organizations={organizations}
              onSelectOrg={(org) => setSelectedOrg(org)}
              onAddOrg={onAddOrg}
              onEditOrg={onEditOrg}
              onDeletedOrg={onDeleteOrg}
            />
        )}

        {global_role === "platform_admin" &&
          activeSection === "organizations" &&
          selectedOrg && (
            <OrganizationDetail
              org={selectedOrg}
              onBack={() => setSelectedOrg(null)}
              onAddUser={onAddUser}
              onEditUser={onEditUser}
              onDeleteUser={onDeleteUser}
              onAddSite={onAddSite}
              onEditSite={onEditSite}
              onDeleteSite={onDeleteSite}
              onSelectSite={(orgId, siteId) => {
                setActiveOrg(orgId);
                setActiveSite(siteId);
                localStorage.setItem("activeOrgId", String(orgId));
                localStorage.setItem("activeSiteId", String(siteId));
                setActiveSection("cameras");
              }}
            />
        )}

        {/* ===== PLATFORM ADMIN : USERS ===== */}
        {global_role === "platform_admin" && activeSection === "users" && (
          <UserManager
            currentUser={currentUser}
            organizations={organizations}
          />
        )}

        {/* ===== PLATFORM ADMIN : SETTINGS ===== */}
        {global_role === "platform_admin" && activeSection === "settings" && (
          <PlatformSettings />
        )}

	{/* ===== PLATFORM ADMIN : SYSTEM HEALTH ===== */}
	{global_role === "platform_admin" &&
	  activeSection === "system_health" && (
	    <SystemHealth />
	)}


	{/* ===== USERS MANAGEMENT (ORG ADMIN) ===== */}
	{global_role === "org_admin" && activeSection === "users" && activeOrgId && (
	  <UsersTab
	    org={organizations.find(o => o.id === activeOrgId)}
	    currentUser={currentUser}
	    onAddUser={onAddUser}
	    onEditUser={onEditUser}
	    onDeleteUser={onDeleteUser}
	  />
	)}


        {/* ===== SITE MANAGEMENT ===== */}
        {activeSection === "sites" && activeOrgId && (
          <SitesTab
            org={organizations.find(o => o.id === activeOrgId)}
            currentUser={currentUser}
            onSelectSite={(orgId, siteId) => {
              setActiveOrg(orgId);
              setActiveSite(siteId);
              localStorage.setItem("activeOrgId", String(orgId));
              localStorage.setItem("activeSiteId", String(siteId));
              setActiveSection("cameras");
            }}
            onAddSite={onAddSite}
            onEditSite={onEditSite}
            onDeleteSite={onDeleteSite}
	  />
        )}


        {/* ===== CAMERAS ===== */}
        {activeSection === "cameras" && activeSiteId && (
          <CameraWorkspace
            cameras={cameras.filter((c) => c.site === activeSiteId)}
            streamStatus={streamStatus}
            canManageCameras={canManageCameras}

	    onAddCamera={() => {
        	  startAdding();  // ✅ Use the helper!
	      setShowCameraModal(true);
        	}}
        	onEditCamera={(cam) => {
	      startEditing(cam);  // ✅ Use the helper that sets site!
	      setShowCameraModal(true);
        	}}

            onDeleteCamera={async (id) => {
              if (!window.confirm("Delete this camera?")) return;
              await authAxios.delete(`/cameras/${id}/`);
              fetchCameras();
            }}
          />
        )}

        {/* ===== EVENTS ===== */}
        {activeSection === "events" && activeSiteId && (
          <EventsSection
            cameras={cameras.filter((c) => c.site === activeSiteId)}
            sites={sites}
          />
        )}
      </main>

      {/* ===== CAMERA MODAL ===== */}
      <CameraModal
        showModal={showCameraModal}
        setShowModal={setShowCameraModal}
        editMode={editMode}
        form={form}
        setForm={setForm}
        organizations={organizations}
        sites={sites.filter((s) => s.organization === activeOrgId)}
        users={users}
        handleAddOrEditCamera={async () => {
          await handleAddOrEditCamera();
          setShowCameraModal(false);
        }}
        loading={loading}
      />

      {/* ===== MOBILE QUICK NAV ===== */}
      <nav
        className={`fixed bottom-0 left-0 right-0 flex justify-around py-3 border-t md:hidden ${
          theme === "dark"
            ? "bg-gray-900 border-gray-700 text-gray-200"
            : "bg-white border-gray-200 text-gray-800"
        }`}
      >
        <button
          onClick={() => setActiveSection("cameras")}
          className={`flex flex-col items-center text-xs ${
            activeSection === "cameras" ? "text-blue-500" : ""
          }`}
        >
          <FiCamera className="text-lg" />
          Cameras
        </button>

        <button
          onClick={() => setActiveSection("events")}
          className={`flex flex-col items-center text-xs ${
            activeSection === "events" ? "text-blue-500" : ""
          }`}
        >
          <FiVideo className="text-lg" />
          Events
        </button>
      </nav>
    </div>
  );
}
